import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import ProductDetail from './components/ProductDetail';
import CustomizationForm from './components/CustomizationForm';
import CategoryPage from './pages/CategoryPage';
import AllProducts from './pages/AllProducts';
import AdminLogin from './components/AdminLogin';
import BlogPage from './pages/BlogPage';
import PostDetail from './pages/PostDetail';
import PageView from './pages/PageView';
import PaymentResult from './pages/PaymentResult';
import AccountPage from './pages/AccountPage';
import CartPage from './pages/CartPage';
import { FeatureGate, captureUtm, readUtm } from './tenant';
import ComparePage from './pages/ComparePage';
import { SiteHeader, SiteFooter, SiteLoader, CustomerProvider } from './sections/SiteChrome';
import QuickModals from './components/QuickModals';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AiChat from './components/AiChat';
import FontLoader from './components/FontLoader';
import SiteHead from './components/SiteHead';
import Tracker from './components/Tracker';
import ThemeProvider from './components/ThemeProvider';
import axios from 'axios';
import { API_BASE } from './config';
// پنل ادمین با lazy-load جدا می‌شود تا بازدیدکننده فروشگاه آن را دانلود نکند
const AdminProductList = React.lazy(() => import('./components/AdminProductList'));
const AdminInventory = React.lazy(() => import('./components/AdminInventory'));
const AdminLoyalty = React.lazy(() => import('./components/AdminLoyalty'));
const AdminTheme = React.lazy(() => import('./components/AdminTheme'));
const AdminProductForm = React.lazy(() => import('./components/AdminProductForm'));
const AdminHomeBuilder = React.lazy(() => import('./components/AdminHomeBuilder'));
const AdminUsers = React.lazy(() => import('./components/AdminUsers'));
const AdminOrders = React.lazy(() => import('./components/AdminOrders'));
const AdminTransactions = React.lazy(() => import('./components/AdminTransactions'));
const AdminDeposits = React.lazy(() => import('./components/AdminDeposits'));
const AdminShipping = React.lazy(() => import('./components/AdminShipping'));
const AdminDiscounts = React.lazy(() => import('./components/AdminDiscounts'));
const AdminBlog = React.lazy(() => import('./components/AdminBlog'));
const AdminBlogCategories = React.lazy(() => import('./components/AdminBlogCategories'));
const AdminComments = React.lazy(() => import('./components/AdminComments'));
const AdminSettings = React.lazy(() => import('./components/AdminSettings'));
const AdminInvoiceTemplate = React.lazy(() => import('./components/AdminInvoiceTemplate'));
const AdminCategory = React.lazy(() => import('./components/AdminCategory'));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const AdminSiteBuilder = React.lazy(() => import('./components/AdminSiteBuilder'));
const AdminPagesList = React.lazy(() => import('./components/AdminPages').then(m => ({ default: m.AdminPagesList })));
const AdminPageEditor = React.lazy(() => import('./components/AdminPages').then(m => ({ default: m.AdminPageEditor })));
const AdminForms = React.lazy(() => import('./components/AdminForms'));
const AdminFormResponses = React.lazy(() => import('./components/AdminFormResponses'));
const AdminGateways = React.lazy(() => import('./components/AdminGateways'));
const AdminAnalytics = React.lazy(() => import('./components/AdminAnalytics'));
function AdminFallback() {
  return (
    <div dir="rtl" className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-4 py-6">
      <div className="h-7 w-48 animate-pulse rounded-xl bg-muted" />
      <div className="h-4 w-72 animate-pulse rounded-lg bg-muted" />
      <div className="h-64 w-full animate-pulse rounded-2xl bg-muted" />
    </div>
  );
}

const NoPlanAccess = (
  <div style={{ maxWidth: 520, margin: '60px auto', padding: '36px 28px', textAlign: 'center', background: '#fff', borderRadius: 18, boxShadow: '0 8px 30px rgba(0,0,0,0.07)', direction: 'rtl' }}>
    <div style={{ fontSize: '2.2rem', marginBottom: 8 }}>🔒</div>
    <div style={{ fontWeight: 800, fontSize: '1.05rem', marginBottom: 6 }}>این بخش در پکیج سایت شما فعال نیست</div>
    <div style={{ fontSize: '0.85rem', color: '#71717A' }}>برای فعال‌سازی با پشتیبانی سایت‌ساز در تماس باشید.</div>
  </div>
);

axios.defaults.baseURL = API_BASE;
axios.defaults.headers.common['Accept'] = 'application/json';

/** Admin route guard: no/invalid token → login (prevents dashboard + stats errors for guests). */
function RequireAdmin({ children }) {
  const loc = useLocation();
  const [state, setState] = React.useState(() => {
    try { return localStorage.getItem('admin_token') ? 'checking' : 'no'; } catch { return 'no'; }
  });
  React.useEffect(() => {
    captureUtm(); // ذخیره utm ورودی جلسه (یک‌بار)
    try { // پینگ بازدید برای آمار پنل سایت‌ساز (sendBeacon، بدون اثر روی سرعت)
      const seen = sessionStorage.getItem('sb_visit_session');
      fetch('/api/site-settings').then(r => r.json()).then(d => {
        const slug = d && d.tenant && d.tenant.slug;
        if (!slug) return;
        if (!seen) { try { sessionStorage.setItem('sb_visit_session', '1'); } catch {} }
        const body = JSON.stringify({ slug, new_session: !seen, path: window.location.pathname.slice(0, 200), referrer: document.referrer.slice(0, 255), ...(readUtm ? readUtm() : {}) });
        if (navigator.sendBeacon) navigator.sendBeacon('https://sitebuilder.jmsoft.ir/api/visits', body);
      }).catch(() => {});
    } catch {}
    if (state !== 'checking') return;
    let alive = true;
    (async () => {
      try {
        const t = localStorage.getItem('admin_token') || '';
        const r = await fetch(API_BASE + '/admin/me', { headers: { Authorization: 'Bearer ' + t } });
        if (!alive) return;
        if (r.ok) { setState('ok'); return; }
      } catch {}
      if (!alive) return;
      try { localStorage.removeItem('admin_token'); } catch {}
      setState('no');
    })();
    return () => { alive = false; };
  }, [state]);
  if (state === 'no') return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;
  if (state !== 'ok') return null;
  return children;
}

export default function App() {
  const isAdmin = window.location.pathname.startsWith('/admin');
  return (
    <Router>
      <CustomerProvider>
      <FontLoader />
      <SiteHead />
      <Tracker />
      {!isAdmin && <ThemeProvider />}
      {!isAdmin && <SiteLoader />}
      {!isAdmin && <SiteHeader />}
      {!isAdmin && <QuickModals />}
      <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" style={{ zIndex: 99999 }} />
      <main>
        <React.Suspense fallback={isAdmin ? <AdminFallback /> : null}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/category/:slug" element={<CategoryPage />} />
          <Route path="/products" element={<AllProducts />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/customize/:productId" element={<CustomizationForm />} />
          <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/admin/products" element={<RequireAdmin><AdminProductList /></RequireAdmin>} />
          <Route path="/admin/inventory" element={<RequireAdmin><AdminInventory /></RequireAdmin>} />
          <Route path="/admin/loyalty" element={<RequireAdmin><AdminLoyalty /></RequireAdmin>} />
          <Route path="/admin/theme" element={<RequireAdmin><AdminTheme /></RequireAdmin>} />
          <Route path="/admin/home" element={<RequireAdmin><AdminHomeBuilder /></RequireAdmin>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/products/new" element={<RequireAdmin><AdminProductForm /></RequireAdmin>} />
          <Route path="/admin/products/:id/edit" element={<RequireAdmin><AdminProductForm /></RequireAdmin>} />
          <Route path="/admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
          <Route path="/admin/orders" element={<RequireAdmin><AdminOrders /></RequireAdmin>} />
          <Route path="/admin/transactions" element={<RequireAdmin><AdminTransactions /></RequireAdmin>} />
          <Route path="/admin/deposits" element={<RequireAdmin><AdminDeposits /></RequireAdmin>} />
          <Route path="/admin/shipping" element={<RequireAdmin><AdminShipping /></RequireAdmin>} />
          <Route path="/admin/discounts" element={<RequireAdmin><AdminDiscounts /></RequireAdmin>} />
          <Route path="/admin/blog" element={<RequireAdmin><AdminBlog /></RequireAdmin>} />
          <Route path="/admin/blog-categories" element={<RequireAdmin><AdminBlogCategories /></RequireAdmin>} />
          <Route path="/admin/comments" element={<RequireAdmin><AdminComments /></RequireAdmin>} />
          <Route path="/admin/settings" element={<RequireAdmin><AdminSettings /></RequireAdmin>} />
          <Route path="/admin/invoice-template" element={<RequireAdmin><AdminInvoiceTemplate /></RequireAdmin>} />
          <Route path="/admin/categories" element={<RequireAdmin><AdminCategory /></RequireAdmin>} />
          <Route path="/admin/layout" element={<RequireAdmin><AdminSiteBuilder /></RequireAdmin>} />
          <Route path="/admin/pages" element={<RequireAdmin><AdminPagesList /></RequireAdmin>} />
          <Route path="/admin/pages/:id" element={<RequireAdmin><AdminPageEditor /></RequireAdmin>} />
          <Route path="/admin/forms" element={<RequireAdmin><AdminForms /></RequireAdmin>} />
          <Route path="/admin/form-responses" element={<RequireAdmin><AdminFormResponses /></RequireAdmin>} />
          <Route path="/admin/gateways" element={<RequireAdmin><AdminGateways /></RequireAdmin>} />
          <Route path="/admin/analytics" element={<RequireAdmin><AdminAnalytics /></RequireAdmin>} />
          <Route path="/payment-result" element={<PaymentResult />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/blog" element={<FeatureGate feature="blog" fallback={NoPlanAccess}><BlogPage /></FeatureGate>} />
          <Route path="/blog/:slug" element={<FeatureGate feature="blog" fallback={NoPlanAccess}><PostDetail /></FeatureGate>} />
          <Route path="/:slug" element={<PageView />} />
        </Routes>
        </React.Suspense>
      </main>
      {!isAdmin && <SiteFooter />}
      {!isAdmin && <AiChat />}
      </CustomerProvider>
    </Router>
  );
}